// Account/AdminUserItem.tsx

import React from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
} from "native-base";
import { Edit3, Trash2 } from "lucide-react-native";
import { DeanteUser } from "./interfaces";

const ORDERED_OTHER_KEYS = ["organization", "phone", "address"];

interface AdminUserItemProps {
  user: DeanteUser;
  onEditUser: (user: DeanteUser) => void;
  onDeleteUser: (user: DeanteUser) => void;
}

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  return value.length > 16 ? value.slice(0, 16) : value;
};

const AdminUserItem: React.FC<AdminUserItemProps> = ({
  user,
  onEditUser,
  onDeleteUser,
}) => {
  const otherInfo =
    user.other_info &&
    typeof user.other_info === "object" &&
    !Array.isArray(user.other_info)
      ? (user.other_info as Record<string, any>)
      : null;

  let otherEntries: [string, string][] = [];

  if (otherInfo) {
    const ordered: [string, string][] = ORDERED_OTHER_KEYS
      .filter((k) => otherInfo[k])
      .map((k) => [k, String(otherInfo[k])]);

    const rest: [string, string][] = Object.entries(otherInfo)
      .filter(
        ([key, value]) =>
          !ORDERED_OTHER_KEYS.includes(key) && value != null && value !== ""
      )
      .map(([k, v]) => [k, String(v)]);

    otherEntries = [...ordered, ...rest];
  }

  const hasOtherInfo = otherEntries.length > 0;

  return (
    <Box px={3} py={2} borderBottomWidth={1} borderColor="gray.200">
      <HStack justifyContent="space-between" alignItems="center" space={2}>
        <VStack flex={1}>
          <Text fontWeight="semibold">{user.login}</Text>

          <HStack space={2} alignItems="center" mt={1}>
            <Badge
              colorScheme={user.role === "admin" ? "purple" : "blue"}
              _text={{ fontSize: "xs" }}
            >
              {user.role}
            </Badge>

            <Text fontSize="xs" color="gray.500">
              Скидка: {user.discount}%
            </Text>
          </HStack>

          {hasOtherInfo && (
            <VStack mt={1} space={0.5}>
              {otherEntries.map(([key, value]) => (
                <Text key={key} fontSize="xs" color="gray.600">
                  {value}
                </Text>
              ))}
            </VStack>
          )}

          <Text fontSize="xs" color="gray.400" mt={1}>
            Создан: {formatDate(user.date_created)}{" "}
            {user.last_login &&
              `· Последний вход: ${formatDate(user.last_login)}`}
          </Text>
        </VStack>

        <HStack space={1}>
          <IconButton
            variant="ghost"
            size="sm"
            onPress={() => onEditUser(user)}
            icon={<Edit3 size={18} color="#4A5568" />}
          />
          <IconButton
            variant="ghost"
            size="sm"
            onPress={() => onDeleteUser(user)}
            icon={<Trash2 size={18} color="#E53E3E" />}
          />
        </HStack>
      </HStack>
    </Box>
  );
};

export default AdminUserItem;
